// src/components/ui/ProfessionalComponents.js
// Pre-built professional UI components using the design system

import React from 'react';
import { useProfessionalTheme } from './ProfessionalThemeProvider';

// Professional Page Container
export const ProfessionalPageContainer = ({ children, className = '' }) => {
  const { getTailwindClasses } = useProfessionalTheme();
  const baseClasses = getTailwindClasses('pageContainer');
  
  return (
    <div className={`${baseClasses} ${className}`}>
      {children}
    </div>
  );
};

// Professional Page Header
export const ProfessionalPageHeader = ({ title, subtitle, icon, className = '', titleClassName = '', subtitleClassName = '' }) => {
  const { getTailwindClasses } = useProfessionalTheme();
  
  return (
    <div className={`${getTailwindClasses('pageHeader')} ${className}`}>
      <h1 className={`${getTailwindClasses('pageTitle')} ${titleClassName}`}>
        {icon && <span className="mr-3">{icon}</span>}
        {title}
      </h1>
      {subtitle && (
        <p className={`${getTailwindClasses('pageSubtitle')} ${subtitleClassName}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
};

// Professional Card Component
export const ProfessionalCard = ({ 
  children, 
  title, 
  subtitle, 
  badge, 
  className = '',
  hover = true,
  onClick 
}) => {
  const { getTailwindClasses } = useProfessionalTheme();
  const cardClasses = getTailwindClasses('card');
  const hoverClasses = hover ? 'hover:shadow-yellow-400/20 hover:-translate-y-1 hover:border-yellow-400/50' : '';
  const clickClasses = onClick ? 'cursor-pointer' : '';
  
  return (
    <div 
      className={`${cardClasses} ${hoverClasses} ${clickClasses} ${className}`}
      onClick={onClick}
    >
      {(title || badge) && (
        <div className="flex justify-between items-center mb-4">
          <div>
            {title && (
              <h3 className="text-lg font-bold text-yellow-400 mb-1">{title}</h3>
            )}
            {subtitle && (
              <p className="text-sm text-slate-400">{subtitle}</p>
            )}
          </div>
          {badge && (
            <span className="inline-block px-3 py-1 bg-yellow-400 text-slate-900 text-xs font-bold uppercase tracking-wide rounded-full">
              {badge}
            </span>
          )}
        </div>
      )}
      {children}
    </div>
  );
};

// Professional Metric Card
export const ProfessionalMetricCard = ({ 
  label, 
  value, 
  unit = '', 
  change, 
  changeType = 'neutral',
  icon,
  className = '' 
}) => {
  const { getTailwindClasses, getPerformanceColor } = useProfessionalTheme();
  
  const changeColors = {
    positive: 'text-green-400',
    negative: 'text-red-400',
    neutral: 'text-slate-400'
  };
  
  return (
    <div className={`${getTailwindClasses('metricCard')} ${className}`}>
      <div className="flex justify-between items-start mb-2">
        <div className="text-sm font-semibold text-slate-400">{label}</div>
        {icon && <span className="text-yellow-400">{icon}</span>}
      </div>
      <div className="text-2xl font-bold text-white mb-1">
        {value}{unit}
      </div>
      {change && (
        <div className={`text-sm font-medium ${changeColors[changeType]}`}>
          {change}
        </div>
      )}
    </div>
  );
};

// Professional Button Component
export const ProfessionalButton = ({ 
  children, 
  variant = 'primary', 
  size = 'base',
  icon,
  className = '',
  ...props 
}) => {
  const { getTailwindClasses } = useProfessionalTheme();
  const buttonClasses = getTailwindClasses('button', variant);
  
  const sizeClasses = {
    sm: 'px-4 py-2 text-sm',
    base: 'px-6 py-3 text-base',
    lg: 'px-8 py-4 text-lg'
  };
  
  return (
    <button 
      className={`${buttonClasses} ${sizeClasses[size]} ${className}`}
      {...props}
    >
      {icon && <span className="mr-2">{icon}</span>}
      {children}
    </button>
  );
};

// Professional Tab Navigation
export const ProfessionalTabs = ({ tabs, activeTab, onTabChange, className = '' }) => {
  const { getTailwindClasses } = useProfessionalTheme();
  
  return (
    <div className={`${getTailwindClasses('tabs')} ${className}`}>
      {tabs.map(tab => (
        <button
          key={tab.id}
          onClick={() => onTabChange(tab.id)}
          className={
            activeTab === tab.id 
              ? getTailwindClasses('tabActive')
              : getTailwindClasses('tabInactive')
          }
        >
          {tab.icon && <span className="mr-2">{tab.icon}</span>}
          {tab.label}
        </button>
      ))}
    </div>
  );
};

// Professional Badge Component
export const ProfessionalBadge = ({ 
  children, 
  variant = 'primary', 
  size = 'base',
  className = '' 
}) => {
  const { getTailwindClasses } = useProfessionalTheme();
  const badgeClasses = getTailwindClasses('badge', variant);
  
  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    base: 'px-3 py-1 text-xs',
    lg: 'px-4 py-1.5 text-sm'
  };
  
  return (
    <span className={`${badgeClasses} ${sizeClasses[size]} ${className}`}>
      {children}
    </span>
  );
};

// Professional Grid Container
export const ProfessionalGrid = ({ 
  children, 
  columns = { mobile: 1, tablet: 2, desktop: 3, wide: 4 },
  gap = 6,
  className = '' 
}) => {
  const gapClasses = {
    2: 'gap-2',
    4: 'gap-4',
    6: 'gap-6',
    8: 'gap-8'
  };
  
  const gridClasses = `
    grid ${gapClasses[gap]} 
    grid-cols-${columns.mobile} 
    md:grid-cols-${columns.tablet} 
    lg:grid-cols-${columns.desktop} 
    xl:grid-cols-${columns.wide}
  `;
  
  return (
    <div className={`${gridClasses} ${className}`}>
      {children}
    </div>
  );
};

// Professional Select Component
export const ProfessionalSelect = ({ 
  value, 
  onChange, 
  options, 
  placeholder = 'Select option',
  className = '' 
}) => {
  const handleChange = (event) => {
    const selectedValue = event.target.value;
    console.log('[ProfessionalSelect] Value changed to:', selectedValue);
    onChange(selectedValue);
  };

  return (
    <select
      value={value}
      onChange={handleChange}
      className={`
        bg-slate-800 text-white border border-yellow-400 rounded-lg 
        px-4 py-2 text-base font-semibold focus:outline-none 
        focus:ring-2 focus:ring-yellow-400 transition-all duration-200
        ${className}
      `}
    >
      {!value && <option value="">{placeholder}</option>}
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// Professional Loading Spinner
export const ProfessionalSpinner = ({ size = 'base', className = '' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    base: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };
  
  return (
    <div className={`${sizeClasses[size]} ${className}`}>
      <div className="animate-spin rounded-full border-2 border-slate-600 border-t-yellow-400"></div>
    </div>
  );
};

// Professional Alert Component
export const ProfessionalAlert = ({ 
  type = 'info', 
  title, 
  children, 
  onClose,
  className = '' 
}) => {
  const typeStyles = {
    success: 'bg-green-900/20 border-green-500 text-green-100',
    warning: 'bg-yellow-900/20 border-yellow-500 text-yellow-100',
    error: 'bg-red-900/20 border-red-500 text-red-100',
    info: 'bg-blue-900/20 border-blue-500 text-blue-100'
  };
  
  const icons = {
    success: '✅',
    warning: '⚠️',
    error: '❌',
    info: 'ℹ️'
  };
  
  return (
    <div className={`
      border rounded-lg p-4 ${typeStyles[type]} ${className}
    `}>
      <div className="flex justify-between items-start">
        <div className="flex items-start space-x-3">
          <span className="text-lg">{icons[type]}</span>
          <div>
            {title && (
              <h4 className="font-semibold mb-1">{title}</h4>
            )}
            <div>{children}</div>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors"
          >
            ✕
          </button>
        )}
      </div>
    </div>
  );
};

// Professional Progress Bar
export const ProfessionalProgressBar = ({ 
  value, 
  max = 100, 
  label, 
  showValue = true,
  className = '' 
}) => {
  const percentage = Math.min((value / max) * 100, 100);
  
  return (
    <div className={className}>
      {label && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-slate-300">{label}</span>
          {showValue && (
            <span className="text-sm text-slate-400">{value}/{max}</span>
          )}
        </div>
      )}
      <div className="w-full bg-slate-700 rounded-full h-2">
        <div 
          className="bg-gradient-to-r from-yellow-400 to-yellow-500 h-2 rounded-full transition-all duration-300"
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
};

export default {
  ProfessionalPageContainer,
  ProfessionalPageHeader,
  ProfessionalCard,
  ProfessionalMetricCard,
  ProfessionalButton,
  ProfessionalTabs,
  ProfessionalBadge,
  ProfessionalGrid,
  ProfessionalSelect,
  ProfessionalSpinner,
  ProfessionalAlert,
  ProfessionalProgressBar
};
